import React from 'react';
import styled from 'styled-components';
import { StaticQuery, graphql } from 'gatsby';
import { Spring } from 'react-spring/renderprops';
import VisibilitySensor from 'react-visibility-sensor';

import Video_Lion from '@videos/lion.mp4';

import Layout from '@components/layout';
import SEO from '@components/seo';

import { P } from '@styles/typography';
import { FlexBottom } from '@styles/helpers';
import { mediaMax, mediaMin } from '@styles/sizes';

import Hero, { HeroTitle, HeroSubtitle } from '../components/Hero/Hero';
import Container from '@components/Container/Container';
import { Background } from '@components/Background/Background';
import Panel, {
  PanelRow,
  PanelTitle,
  PanelDescription,
  PanelReadMore,
} from '@components/Panel/Panel';
import TestimonialPanel from '@components/Panel/TestimonialPanel';
import Button from '@components/Button/Button';
import Testimonial from '@components/Testimonial/Testimonial';
import Image from '@components/Image/Image';
import Video from '@components/Video/Video';
import PageTransition from '@components/pageTransition';
import {FadeInUp} from '../components/Animations/FadeIn';

const StyledPanels = styled(PanelRow)`
  margin: -150px -16px 100px -16px;
  ${mediaMax.phone`
    margin-top: -100px;
`}
`;

const StyledPanel = styled(Panel)`
  background: #f1f5f2;
  height: 340px;
  flex-basis: 0;
  flex-grow: 1;
  ${mediaMax.tablet`
    height: auto;
    margin-bottom: 16px;
    flex-basis: auto;
  `}
`;

const StyledAboutRow = styled.div`
  display: flex;
  margin-bottom: 80px;
  ${mediaMax.tablet`
    flex-direction: column;
  `}
`;

const StyledAboutRowImages = styled(StyledAboutRow)`
  ${mediaMax.tablet`
    flex-direction: column-reverse;
  `}
`;

const StyledAboutIntro = styled.div`
  width: 450px;
  padding-right: 16px;
  display: flex;
  flex-shrink: 0;
  ${mediaMax.tablet`
    margin: 0 auto;
    width: auto;
  `}
`;

const StyledAboutVideo = styled.div`
  width: 50%;
  max-width: 500px;
  margin-left: auto;
  ${mediaMax.tablet`
    width: 80%;
    max-width: 600px;
    margin: 32px auto 0 auto;
  `}
`;

const StyledVideoWidth = styled(Video)`
  width: 100%;
  ${mediaMin.container`
    width: 48vw;
  `}
`;

const StyledImage = styled(Image)`
  ${mediaMin.tablet`
    width: ${props => props.Width && props.Width};
  `}
`;

const StyledImage2 = styled(StyledImage)`
  ${mediaMin.tablet`
    padding-left: 32px;
  `}
`;

const StyledTestimonialWrapper = styled.div`
  background: #ffffff;
  padding: 32px;
  max-width: 600px;
  position: relative;
  margin-top: -200px;
  ${mediaMin.tablet`
    margin-left: -100px;
  `}
  ${mediaMax.tablet`
    margin-top: -160px;
    max-width: 80%;
    margin-left: auto;
    margin-right: auto;
  `}
`;

const StyledTestimonial = styled(Testimonial)`
  font-size: 18px;
  ${mediaMax.tablet`
    font-size: 16px;
  `}
`;

const StyledProjectWrapper = styled.div`
  margin-bottom: 80px;
`;

const StyledProjectIntroWrapper = styled.div`
  ${mediaMin.tablet`
    width: 450px;
    margin-left: auto;
    padding-left: 32px;
  `}
`;

const StyledProjectRow = styled.div`
  display: flex;
  margin-bottom: 40px;
  ${mediaMax.tablet`
    flex-direction: column;
  `}
`;

const StyledProjectIntro = styled.div`
  margin-bottom: 40px;
`;

const StyledProjectImage1 = styled(Image)`
  max-width: 450px;
  ${mediaMax.tablet`
    display: none;
  `}
`;

const IndexPage = () => (
  <StaticQuery
    query={graphql`
      query Homepage {
        site {
          siteMetadata {
            testimonials {
              content
              author
            }
            blocks {
              wild
              life
              film
            }
          }
        }
      }
    `}
    render={data => (
      <Layout page={'Home'}>
        <SEO
          title="Home"
          keywords={[`reel nature`, 'film', 'logistics', 'crew support']}
        />
        <PageTransition>
          <Hero>
            <FadeInUp>
              <HeroTitle>Reel Nature</HeroTitle>
              <HeroSubtitle>
                The number one choice for film crew support in Zambia and the
                region.
              </HeroSubtitle>
            </FadeInUp>
          </Hero>
          <Container>
            <FadeInUp delay>
              <StyledPanels>
                <StyledPanel>
                  <PanelTitle>Wild.</PanelTitle>
                  <PanelDescription>
                    {data.site.siteMetadata.blocks.wild}
                  </PanelDescription>
                  <PanelReadMore url={'/about/wild'} />
                </StyledPanel>
                <StyledPanel>
                  <PanelTitle>Life.</PanelTitle>
                  <PanelDescription>
                    {data.site.siteMetadata.blocks.life}
                  </PanelDescription>
                  <PanelReadMore url={'/about/life'} />
                </StyledPanel>
                <StyledPanel>
                  <PanelTitle>Film.</PanelTitle>
                  <PanelDescription>
                    {data.site.siteMetadata.blocks.film}
                  </PanelDescription>
                  <PanelReadMore url={'/about/film'} />
                </StyledPanel>
              </StyledPanels>
            </FadeInUp>
          </Container>
          <Background bg={'#F1F5F2'}>
            <Container>
              <StyledAboutRow>
                <StyledAboutIntro>
                  <FlexBottom>
                    <h3>
                      About
                      <br /> Reel Nature
                    </h3>
                    <P>
                      Reel Nature is a Zambian Registered Company, providing
                      film crew support services, fixing and ultimately remote
                      location support, kit and equipment; a one-stop-shop with
                      no need for middlemen! Reel Nature is an offshoot of
                      Jeffery & McKeith Safaris, who have worked with wildlife
                      and documentary film crews since 2012 and offer a highly
                      professional and efficient service from the first
                      researchers email to the final day departure of the film
                      crew.
                    </P>
                    <Button to={'/about/'}>Read our story</Button>
                  </FlexBottom>
                </StyledAboutIntro>
                <StyledAboutVideo>
                  <StyledVideoWidth
                    src={Video_Lion}
                    caption="Lions in the Kafue"
                  />
                </StyledAboutVideo>
              </StyledAboutRow>
              <StyledAboutRowImages>
                <StyledImage Width={'60%'} src={'safari-drone.jpg'} />
                <StyledImage2 src={'safari-cheetah.jpg'} Width={'40%'} />
              </StyledAboutRowImages>

              <VisibilitySensor partialVisibility>
                {({ isVisible }) => (
                  <FadeInUp isVisible={isVisible}>
                      <StyledTestimonialWrapper>
                        <StyledTestimonial
                          testimonial={data.site.siteMetadata.testimonials[1]}
                        />
                      </StyledTestimonialWrapper>
                  </FadeInUp>
                )}
              </VisibilitySensor>
            </Container>
          </Background>
          <Container>
            <StyledProjectWrapper>
              <StyledProjectRow>
                <StyledProjectImage1
                  src={'rig.jpg'}
                  caption={'The Reel Nature team on Location'}
                />
                <StyledProjectIntroWrapper>
                  <StyledProjectIntro>
                    <h3>
                      Reel Nature
                      <br /> Reel
                    </h3>
                    <P>
                      We have been extremely lucky to have worked with some of
                      the finest wildlife film makers and production companies
                      in the world. Having a good rapport with the team on the
                      ground here in Zambia is hugely important and hopefully
                      the following testimonials from our shoots to date
                      highlight this. We look forward to helping make your next
                      shoot come to fruition here in Zambia, one of the safest
                      and most rewarding places to shoot ‘Reel Nature’!
                    </P>
                    <Button to={'/projects/'}>Projects</Button>
                  </StyledProjectIntro>
                  <Image
                    src={'on-location.jpg'}
                    caption={'Filming for Dangerous Grounds'}
                  />
                </StyledProjectIntroWrapper>
              </StyledProjectRow>
              <StyledProjectRow>
                <Image
                  src={'safari-male-lion.jpg'}
                  maxWidth={800}
                  caption={'Planet Earth II footage'}
                />
              </StyledProjectRow>
            </StyledProjectWrapper>
          </Container>
          <TestimonialPanel>
            <Testimonial testimonial={data.site.siteMetadata.testimonials[0]} />
          </TestimonialPanel>
        </PageTransition>
      </Layout>
    )}
  />
);

export default IndexPage;
